import DEFAULT_CARD_PREFERENCES from '../definitions/DefaultCardPreferences';
import replaceShortcodesInRatehubReview from './replaceShortcodesInRatehubReview';
import createCardSynopsisFromAPIResponse from './createCardSynopsisFromAPIResponse';


/**
 * Creates a CreditCardShape from an API response and user data
 * @param {Object} card raw card data from fetch
 * @param {Object} [userInfo=DEFAULT_CARD_PREFERENCES] contextual user information, often as provided by CardFinder wizard
 * @returns {(CreditCardShape|null)} CreditCardShape structure when successful
 *
 */
function createCardInfoFromAPIResponse(card, userInfo = DEFAULT_CARD_PREFERENCES) {
    // map BE properties to FE’s credit card “cardInfo” structure, calculating derived fields as we go
    return [
        createCommonProperties,
        createCashBackProperties,
    ].reduce((cardProperties, createFcn) => ({
        ...cardProperties,
        ...createFcn(card, userInfo, cardProperties),
    }), {});
}


/**
 * map back-end card properties to “cardInfo” structure credit card property names, with default values for some
 *
 * @param {object} card back-end data for a single credit card
 * @param {object} userInfo contextual user information, typically as provided to CardFinder wizard
 * @return {object} contains “cardInfo” properties for the given card that are common to all product types
 */
function createCommonProperties(card, userInfo) {
    return {
        ...createCardSynopsisFromAPIResponse(card, userInfo),

        isHidden: card.hidden,
        bulletPointSummary: Array.isArray(card.descriptions.summary) ? card.descriptions.summary : [],

        firstYearFee: card.fees.first_year_annual,

        minimumHouseholdIncome: card.eligibility.income.household || 0,

        // API returns out of 5; we need out of 100.
        reviewRating: (card.user_reviews.average_rating || 0) * 20,
        reviewCount: card.user_reviews.review_count,
        reviewURL: card.urls.details + '?view=reviews',
        ratehubReview: createRatehubReviewFromAPIResponse(card),
        reviews: card.user_reviews.reviews.map(review => {
            return {
                name: review.name,
                rating: review.rating,
                comments: review.comments,
                date: new Date(review.date),
            };
        }),

        // TODO remove style removal hack once classic pages are deprecated, and if the style attribute is absent in the CMS
        // TODO conditionally retrieve this only as required (as of now, that means only from a card details page)
        legalMarkup: card.descriptions.legal?.trim().replace(/\sstyle=(['"])[^\\1]*\1/ig, '') || undefined,
    };
}

/**
 * @private
 * Re-shape a review into the format our Component expects
 * @param {Object} card API response for a given credit card
 * @returns {Object|null}
 */
function createRatehubReviewFromAPIResponse(card) {
    const reviewInfo = card.ratehub_review;
    if (!reviewInfo) {
        return null;
    }

    // Reshape it from the API into the way our Component wants it.
    return {
        content: replaceShortcodesInRatehubReview(card),
        rating: reviewInfo.rating,
        reviewAuthor: {
            name: reviewInfo.author?.name,
            jobTitle: reviewInfo.author?.job_title,
            profileURL: reviewInfo.author?.profile_url ,
            avatarImage: reviewInfo.author?.user?.user_avatar,    // currently useless?
            profileImage: reviewInfo.author?.image_url,
        },
        publishedDate: reviewInfo.dates?.published,
        modifiedDate: reviewInfo.dates?.modified,
    };
}


function createCashBackProperties({ point_redemption }, _ignored, { welcomeBonus }) {
    return point_redemption?.copy.cash_back
        ? {
            howToRedeemDescription: point_redemption.copy.cash_back,

            // expected sub-objects: welcome_signup and welcome_spending, each with an optional
            // property 'cash-back-rewards'
            cashBackWelcomeBonus: Object.values(welcomeBonus || {})
                .reduce((sum, entry) => sum + (entry['cash-back-rewards'] || 0), 0),
        }
        : {};
}


export default createCardInfoFromAPIResponse;
